import React, { ChangeEvent, useEffect, useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';

// Types & Enums
import { ButtonThemes } from '../../enums/button-themes';
import { NotificationMessageType } from '../../enums/notification-types';

// Redux
import { useDispatch } from 'react-redux';
import { setModal } from '../../redux/actions/modal';
import { addNotification } from '../../redux/actions/notification';

// Components
import ModalNavigation from '../../components/modal-navigation';
import InputButton from '../../components/inputs/input-button';
import FormSelect from '../../components/form-fields/form-select';
import FormCheckBoxInput from '../../components/form-fields/form-checkbox-input';

// Helpers
import { generatePath, generateUrl } from '../../helpers/request-builder';

import LoadingSpinner from '../../components/loading-spinner';

const StyledAddContainer = styled.div`
    margin-top: 20px;
    background-color: white;
    padding: 1px 20px 20px 20px;
`;

const StyledButtonContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    width: 100%;
`;

type DropdownOption = {
    label: string;
    name: string;
    value: number;
};

const PageLayout = ({ children }: { children: React.ReactNode }) => {
    const dispatch = useDispatch();

    const modalNavigationButtons = [
        {
            value: 'CLOSE',
            onClick: () => dispatch(setModal('ManageImpressionModelling', {})),
            disabled: false,
            buttonTheme: ButtonThemes.Secondary,
        },
    ];

    return (
        <div>
            <ModalNavigation buttons={modalNavigationButtons} />
            <h2>Add Impression Configuration</h2>
            <p>Add a new mapping between an impression source and a channel.</p>
            {children}
        </div>
    );
};

const LayoutModalAddImpressionModelConfig = () => {
    const dispatch = useDispatch();

    const [selectedSource, setSelectedSource] = useState('');
    const [selectedChannel, setSelectedChannel] = useState('');
    const [isActive, setIsActive] = useState(true);
    const [sourceOptions, setSourceOptions] = useState<DropdownOption[]>([]);
    const [channelOptions, setChannelOptions] = useState<DropdownOption[]>([]);
    const [existingConfigs, setExistingConfigs] = useState<{ impressionModelId: number; refererId: number }[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        Promise.all([
            axios({
                method: 'GET',
                url: generateUrl('config', 'impression-model', [{ key: 'limit', value: 0 }]),
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' },
            }),
            axios({
                method: 'GET',
                url: generateUrl('config', 'dash-referer', [
                    { key: 'active', value: 1 },
                    { key: 'order_by', value: 'name' },
                    { key: 'limit', value: 0 },
                ]),
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' },
            }),
            axios({
                method: 'GET',
                url: generateUrl('config', 'impression-model-config', [{ key: 'limit', value: 0 }]),
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' },
            }),
        ])
            .then(([sourceResponse, channelResponse, configResponse]) => {
                setSourceOptions(
                    sourceResponse.data.objects.map((obj: any) => ({
                        label: String(obj.value),
                        name: String(obj.value),
                        value: obj.id,
                    }))
                );
                setChannelOptions(
                    channelResponse.data.objects.map((obj: any) => ({
                        label: String(obj.name),
                        name: String(obj.name),
                        value: obj.id,
                    }))
                );
                setExistingConfigs(
                    configResponse.data.objects.map((obj: any) => ({
                        impressionModelId: obj.impression_model.id,
                        refererId: obj.referer.id,
                    }))
                );
                setIsLoading(false);
            })
            .catch(() => {
                setIsLoading(false);
            });
    }, []);

    const handleChangeSource = (event: ChangeEvent<HTMLInputElement>) => {
        setSelectedSource(event.target.value);
    };

    const handleChangeChannel = (event: ChangeEvent<HTMLInputElement>) => {
        setSelectedChannel(event.target.value);
    };

    const isDuplicate =
        selectedSource !== '' &&
        selectedChannel !== '' &&
        existingConfigs.some(
            config =>
                config.impressionModelId === Number(selectedSource) && config.refererId === Number(selectedChannel)
        );

    const handleOnSaveClick = () => {
        if (isDuplicate) {
            dispatch(
                addNotification({
                    copy: 'This source and channel combination already exists.',
                    type: NotificationMessageType.Error,
                })
            );
            return;
        }

        setIsSaving(true);
        axios({
            method: 'POST',
            url: generateUrl('config', 'impression-model-config'),
            data: {
                impression_model: generatePath('config', 'impression-model', selectedSource),
                referer: generatePath('config', 'referer', selectedChannel),
                active: isActive,
            },
            withCredentials: true,
            headers: { 'Content-Type': 'application/json' },
        })
            .then(() => {
                dispatch(setModal('ManageImpressionModelling', {}));
                dispatch(
                    addNotification({
                        copy: 'Impression configuration added successfully.',
                        type: NotificationMessageType.Success,
                    })
                );
            })
            .catch(error => {
                const isDuplicate =
                    error?.response?.data?.error_message?.includes('Duplicate entry') ||
                    error?.response?.data?.message?.includes('Duplicate entry') ||
                    error?.response?.status === 409;
                dispatch(
                    addNotification({
                        copy: isDuplicate
                            ? 'This source and channel combination already exists.'
                            : 'There was an issue while adding the configuration. Please try again later.',
                        type: NotificationMessageType.Error,
                    })
                );
            })
            .finally(() => setIsSaving(false));
    };

    if (isLoading) {
        return (
            <PageLayout>
                <LoadingSpinner />
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <StyledAddContainer>
                <FormSelect
                    label="Source:"
                    inputKeyValue="source"
                    inputOptions={sourceOptions}
                    inputOnChange={handleChangeSource}
                    requiredField={true}
                    inputValue={selectedSource}
                    errorMessage={selectedSource === '' ? 'Source is required.' : ''}
                />

                <FormSelect
                    label="Channel:"
                    inputKeyValue="channel"
                    inputOptions={channelOptions}
                    inputOnChange={handleChangeChannel}
                    requiredField={true}
                    inputValue={selectedChannel}
                    errorMessage={selectedChannel === '' ? 'Channel is required.' : ''}
                />

                <FormCheckBoxInput
                    inputKeyValue="active"
                    inputName="active"
                    label="Active"
                    checked={isActive}
                    inputOnChange={() => setIsActive(prev => !prev)}
                />

                {isDuplicate && (
                    <p style={{ color: 'red', marginBottom: '10px' }}>
                        This source and channel combination already exists.
                    </p>
                )}

                <StyledButtonContainer>
                    <InputButton
                        value="SAVE"
                        onClick={handleOnSaveClick}
                        isLoading={isSaving}
                        disabled={selectedSource === '' || selectedChannel === '' || isDuplicate}
                    />
                </StyledButtonContainer>
            </StyledAddContainer>
        </PageLayout>
    );
};

export default LayoutModalAddImpressionModelConfig;
